<template>
  <canvas ref="canvasRef" />
</template>
<script lang="ts" setup>
import { watch, ref, onMounted, computed } from 'vue';
import { decode } from 'blurhash';

const props = withDefaults(
  defineProps<{
    hash: string;
    dimensions?: { width: number; height: number };
  }>(),
  {
    hash: '',
    dimensions: () => ({ width: 0, height: 0 }),
  }
);

const canvasRef = ref<HTMLCanvasElement>();
const defaultHeight = 10;
const defaultWidth = 10;

const minimizedDimensions = computed(() => {
  const { height, width } = props.dimensions || {};
  if (!height || !width || isNaN(height) || isNaN(width)) {
    return { height: defaultHeight, width: defaultWidth };
  }

  const aspectRatio = width / height;
  const newWidth = Math.max(defaultWidth, Math.round(aspectRatio * 10));
  const newHeight = Math.round(newWidth / aspectRatio);

  return { width: newWidth, height: newHeight };
});

const decodeHash = () => {
  const { width, height } = minimizedDimensions.value;
  const pixels = decode(props.hash, width, height);
  if (!canvasRef.value) {
    return;
  }
  const ctx = canvasRef.value.getContext('2d');
  if (!ctx) {
    return;
  }
  canvasRef.value.width = width;
  canvasRef.value.height = height;
  const imageData = ctx.createImageData(width, height);
  imageData.data.set(pixels);
  ctx.putImageData(imageData, 0, 0);
};

watch(
  () => props.hash,
  (oldValue: string, newValue: string) => {
    if (newValue && oldValue !== newValue) {
      decodeHash();
    }
  }
);

onMounted(() => {
  if (!props.hash) {
    return;
  }
  decodeHash();
});
</script>

<style scoped>
@import '@getaccept/lib-shared/src/editor/css/editor-variables.css';

canvas {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  border-radius: var(--editor-medium-border-radius);
}
</style>
